/* The month grid of the route editor.
   Normative rules: docs/design/spec/. No mockup exists for this screen — `merch-detail.html` shows
   only the read-only Routes tab and its "Open route editor" button — so the frame is antd's and the
   cell body is ours.

   antd `<Calendar>` owns the grid: locale weekday headers, the Monday-first column order (from
   `src/util/dayjs.js`), today, out-of-month cells and real `<table>` semantics. We supply
   `headerRender`, because antd's default header is a year + month `<Select>` pair and this editor
   steps one month or one week at a time, and `cellRender`, which is where the stops and the drop
   target live.

   The WEEK view is the same month grid with the five other rows hidden (`rc-calendar--week` +
   `:has(.rc-cell--in)` in style.scss). antd has no week mode, and its six Monday-started rows
   always contain the row of any in-month date, so one grid serves both — no second component, no
   second set of drag-and-drop handlers (PICSELL-1615 #8). In week view the days either side of the
   month that fall inside the week are real, droppable days.

   Two things a supervisor can do to a day, and BOTH must work:
   - drag a point onto it (mouse), and
   - select a point in the panel and activate this day (touch + keyboard).
   Native HTML5 drag fires no events on a touch screen and is unreachable from the keyboard, so the
   second path is not a convenience — see docs/design/spec/03-patterns.md#accessibility-floors. */

import React, { useState } from "react";
import { Button, Calendar, Flex, Segmented, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, DragOutlined, LeftOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";

import { ROUTE_CALENDAR } from "../../../config/lang";
import { translate } from "../../../util/util";
import dayjs from "../../../util/dayjs";
import { addDays, isSameMonth, monthLabel, nthOfMonth, startOfWeek, weekLabel, ymd } from "./dateUtils";
import { isDateKey, isWeekKey, mergedStopsForDate } from "./routeModel";
import { POINT_DND_TYPE } from "./PointSearchPanel";

const ORDINAL_KEY = { 1: "ord_first", 2: "ord_second", 3: "ord_third", 4: "ord_fourth", 5: "ord_fifth" };

/* A stop dragged from one day to another carries its layer and source date as well as the point —
   what leaves the source depends on both (routeModel.moveStop). A separate type from the panel's
   so a drop can tell "add" from "move" (PICSELL-1626 #4). */
export const STOP_DND_TYPE = "application/x-picsell-route-stop";

/* The absence covering a date, if any. Absence.start/end are datetimes; date-only compare keeps
   both boundary days inside the range. */
const absenceFor = (absences, date) => {
  const ds = ymd(date);
  return (absences || []).find(a => a.start && a.end && String(a.start).slice(0, 10) <= ds && ds <= String(a.end).slice(0, 10)) || null;
};

/**
 * Which layer a stop came from, as a `<Tag>`.
 *
 * The three layers are additive and a day shows all of them at once, so the badge is the only thing
 * saying whether removing this stop affects one date or every matching weekday. It is a word, not
 * just a colour (spec/01: colour is never the only signal).
 */
const layerBadge = (stop, date, lang) => {
  if (isDateKey(stop.layerKey)) return { text: translate(ROUTE_CALENDAR, "badge_date", lang), tone: "date" };
  if (isWeekKey(stop.layerKey)) return { text: translate(ROUTE_CALENDAR, ORDINAL_KEY[nthOfMonth(date)], lang), tone: "nth" };
  return { text: translate(ROUTE_CALENDAR, "badge_weekly", lang), tone: "weekly" };
};

function DayCell({ date, inMonth, layers, lang, armed, moving, absence, onDrop, onMoveDrop, onPlace, onRemove, onMove, onStartMove }) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  const [over, setOver] = useState(false);
  const stops = inMonth ? mergedStopsForDate(date, layers) : [];

  /* An absence day refuses NEW stops (drop + place) but keeps showing what is already planned,
     and remove/move stay available — the lock is on assignment, not on cleanup (#1581 #14). */
  const locked = Boolean(absence);

  const handleDrop = event => {
    event.preventDefault();
    setOver(false);
    if (!inMonth || locked) return;
    try {
      const stop = event.dataTransfer.getData(STOP_DND_TYPE);
      if (stop) return onMoveDrop(date, JSON.parse(stop));
      const raw = event.dataTransfer.getData(POINT_DND_TYPE);
      if (raw) onDrop(date, JSON.parse(raw));
    } catch (_) {
      /* ignore malformed payloads */
    }
  };

  return (
    <div
      className={`rc-cell${inMonth ? " rc-cell--in" : ""}${over ? " rc-cell--over" : ""}${armed && inMonth && !locked ? " rc-cell--armed" : ""}${
        locked && inMonth ? " rc-cell--absent" : ""
      }`}
      onDragOver={event => {
        if (!inMonth || locked) return;
        event.preventDefault();
        /* Must match the source's `effectAllowed`, or the browser refuses the drop: a stop moves,
           a panel point copies. */
        event.dataTransfer.dropEffect = [...event.dataTransfer.types].includes(STOP_DND_TYPE) ? "move" : "copy";
        if (!over) setOver(true);
      }}
      onDragLeave={() => over && setOver(false)}
      onDrop={handleDrop}>
      <Flex vertical gap={4} className="rc-cell__stops">
        {stops.map((stop, index) => {
          const badge = layerBadge(stop, date, lang);
          /* A stop only reorders within its OWN layer — the base order is the recurring route's
             order and moving across layers would mean nothing. */
          const canUp = index > 0 && stops[index - 1].layerKey === stop.layerKey;
          const canDown = index < stops.length - 1 && stops[index + 1].layerKey === stop.layerKey;
          return (
            /* Two lines, name first. A month grid gives each column ~115px, which a badge, a name
               and three buttons cannot share — measured at the earlier ~84px, the name collapsed
               to 0px wide. The name is what is being read, so it gets the full width and the
               badge sits under it. */
            <div
              className={`rc-stop${moving && moving.pointId === stop.point.id && moving.ds === ymd(date) ? " rc-stop--moving" : ""}`}
              key={`${stop.layerKey}_${stop.point.id}`}
              draggable
              onDragStart={event => {
                event.dataTransfer.setData(STOP_DND_TYPE, JSON.stringify({ point: stop.point, layerKey: stop.layerKey, from: ymd(date) }));
                event.dataTransfer.effectAllowed = "move";
              }}>
              {/* Two lines before the ellipsis: at ~115px a month column cut most names to their
                  network ("Сільпо - Старокос…"), and the tooltip showed the ADDRESS, so hovering
                  did not recover the name either (PICSELL-1626 #3). `Paragraph`, not `Text`: antd's
                  Text ellipsis is single-line only and silently ignores `rows`. */}
              <Typography.Paragraph
                className="rc-stop__name"
                ellipsis={{ rows: 2, tooltip: [stop.point.name, stop.point.address].filter(Boolean).join(" · ") }}>
                {stop.point.name}
              </Typography.Paragraph>
              <Tag variant="filled" className={`rc-badge rc-badge--${badge.tone}`}>
                {badge.text}
              </Tag>
              {/* Absolutely positioned: `opacity: 0` still occupies space, and these three were
                  what squeezed the name out. */}
              <Flex className="rc-stop__acts" gap={0}>
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={!canUp}
                  aria-label={t("move_up")}
                  onClick={() => onMove(stop.layerKey, stop.point.id, -1)}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={!canDown}
                  aria-label={t("move_down")}
                  onClick={() => onMove(stop.layerKey, stop.point.id, 1)}
                />
                {/* The keyboard/touch half of dragging a stop elsewhere: arms it, and every other day
                    offers "Add here" exactly as for a point picked in the panel. */}
                <Button
                  type="text"
                  size="small"
                  icon={<DragOutlined />}
                  aria-label={t("move_stop")}
                  title={t("move_stop")}
                  onClick={() => onStartMove(stop, date)}
                />
                <Button type="text" size="small" icon={<CloseOutlined />} aria-label={t("remove_stop")} onClick={() => onRemove(stop, date)} />
              </Flex>
            </div>
          );
        })}
      </Flex>

      {/* The absence itself, named — a greyed cell alone would not say WHY it refuses the drop
          (colour is never the only signal, spec/01). The reason is user content, shown as-is. */}
      {locked && inMonth ? (
        <Tag variant="filled" className="rc-badge rc-badge--absent" title={absence.reason || undefined}>
          {absence.reason || t("absent_day")}
        </Tag>
      ) : null}

      {/* The keyboard/touch half of "drop a point here". A real <Button>, so it is tabbable and
          Enter/Space work; it exists only while a point is selected, so the grid is not littered
          with affordances that would do nothing. */}
      {armed && inMonth && !locked ? (
        <Button className="rc-cell__place" size="small" type="dashed" block icon={<PlusOutlined />} onClick={() => onPlace(date)}>
          {t("add_here")}
        </Button>
      ) : null}
    </div>
  );
}

export default function RouteCalendar({
  monthDate,
  view = "month",
  onView,
  layers,
  lang,
  armed,
  moving,
  absences,
  onPrev,
  onNext,
  onDrop,
  onMoveDrop,
  onPlace,
  onRemove,
  onMove,
  onStartMove,
}) {
  const t = key => translate(ROUTE_CALENDAR, key, lang);
  /* Week view: the week containing the cursor, Monday first. The panel is given the MONDAY, so
     antd draws the Monday's month and that week is always one of its six rows — the cursor itself
     stays where the month view left it (see Route.js). */
  const week = view === "week";
  const weekStart = startOfWeek(monthDate);
  const weekEnd = addDays(weekStart, 7);
  const inView = date => (week ? weekStart <= date && date < weekEnd : isSameMonth(date, monthDate));
  /* antd works in dayjs; the route model works in native `Date` (its keys are built from local
     Y-M-D, and the backend runs with USE_TZ=False). `.toDate()` is the boundary, once per cell. */
  const value = dayjs(week ? weekStart : monthDate);

  return (
    <div className={`rc-calendar${week ? " rc-calendar--week" : ""}`}>
      <Calendar
        value={value}
        /* The panel is driven by the toolbar's stepper, not by clicking a day — a day click is
           "place the selected point here", which the cell's own button owns. */
        onSelect={() => {}}
        headerRender={() => (
          <Flex className="rc-cal__head" align="center" justify="center" gap="small" wrap>
            <Button type="text" icon={<LeftOutlined />} aria-label={t(week ? "prev_week" : "prev_month")} onClick={onPrev} />
            <Typography.Text strong className="rc-cal__month">
              {week ? weekLabel(weekStart, lang) : monthLabel(monthDate, lang)}
            </Typography.Text>
            <Button type="text" icon={<RightOutlined />} aria-label={t(week ? "next_week" : "next_month")} onClick={onNext} />
            <Segmented
              size="small"
              className="rc-cal__view"
              value={view}
              onChange={onView}
              options={[
                { value: "month", label: t("view_month") },
                { value: "week", label: t("view_week") },
              ]}
            />
          </Flex>
        )}
        cellRender={(current, info) => {
          if (info.type !== "date") return info.originNode;
          const date = current.toDate();
          return (
            <DayCell
              date={date}
              inMonth={inView(date)}
              layers={layers}
              lang={lang}
              armed={armed}
              moving={moving}
              absence={absenceFor(absences, date)}
              onDrop={onDrop}
              onMoveDrop={onMoveDrop}
              onPlace={onPlace}
              onRemove={onRemove}
              onMove={onMove}
              onStartMove={onStartMove}
            />
          );
        }}
      />
    </div>
  );
}
